// sort-imports-ignore
import React, { Suspense } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';

import GpcOptOutBanner from '../components/GpcOptOutBanner';
import Loader from '../components/Loader';
import AuthenticatedRoute from '../components/routeComponents/AuthenticatedRoute';
import LERoute from '../components/routeComponents/LERoute';
import { isLocal } from '../config';
import { LDFlags } from '../constants/flags';
import {
  ACCOUNT_LOGIN,
  CREATE_ACCOUNT,
  DASHBOARD,
  FEEDBACK,
  FLOW_ENTRY,
  FORGOT_PASSWORD,
  HOME,
  MARKETPLACE_CONDITION,
  MARKETPLACE_PHOTOS,
  MKT_HOME,
  PAYMENTS_DASHBOARD,
  REDIRECT_URLS,
  RESET_PASSWORD,
  PHOTO_CAPTURE,
  SECURE_DATA_ENTRY,
  VERIFY_INSURANCE,
  MARKETPLACE_FEEDBACK,
  WE_LISTEN,
} from '../constants/urls';
import { useFlag } from '../hooks';
import useInactivityLogout from '../hooks/useInactivityLogout';
import PageFor404s from '../pages/404Page';
import AccountLogin from '../pages/auth/AccountLogin';
import CreateAccount from '../pages/auth/CreateAccount';
import ForgotPassword from '../pages/auth/ForgotPassword';
import ResetPassword from '../pages/auth/ResetPassword';
import FlowEntry from '../pages/flowEntryPages/FlowEntry';
import SimpleLayout from '../pages/layouts/SimpleLayout';
import MainLayout from '../pages/layouts/MainLayout';
import FlowLayout from '../pages/layouts/FlowLayout';
import FlowRoutes from './FlowRoutes';
import Feedback from '../pages/Feedback/Feedback';
import PhotoCapture from '../pages/photoCapture/PhotoCapture';
import SecureDataEntry from '../pages/secureDataEntry/SecureDataEntry';
import VerifyInsurance from '../pages/verifyInsurance/VerifyInsurance';
import MarketplaceFeedback from '../pages/marketplaceFeedback/MarketplaceFeedback';
import WeListen from '../pages/weListen/WeListen';
// import ImageLibrary from '../pages/ImageLibrary';

const Dashboard = React.lazy(() => import('../pages/dashboard/RootDashboard'));

const Routes: React.FC = () => {
  useInactivityLogout();
  const { search } = useLocation();

  const secureInfoLinkEnabled = useFlag(LDFlags.SECURE_INFO_LINK);

  return (
    <Switch>
      <Route
        exact
        path={HOME}
        component={() => {
          if (isLocal) {
            window.location.replace(FLOW_ENTRY);
          } else {
            window.location.replace(`${MKT_HOME}${search}`);
          }

          return null;
        }}
      />
      {Object.entries(REDIRECT_URLS).map(([key, value]) => (
        <Route
          key={key}
          path={key}
          component={() => {
            window.location.replace(value);
            return null;
          }}
        />
      ))}
      <Route path="/flow/:path?">
        <FlowLayout>
          <Suspense fallback={<Loader hideText mt={8} />}>
            <FlowRoutes />
          </Suspense>
        </FlowLayout>
      </Route>
      <AuthenticatedRoute
        path={[DASHBOARD, PAYMENTS_DASHBOARD, MARKETPLACE_CONDITION, MARKETPLACE_PHOTOS]}
        component={Dashboard}
        title="Dashboard | Lease End"
        exact
      />
      <Route
        path={[
          ACCOUNT_LOGIN,
          FORGOT_PASSWORD,
          RESET_PASSWORD,
          CREATE_ACCOUNT,
          FEEDBACK,
          ...(secureInfoLinkEnabled ? [SECURE_DATA_ENTRY] : []),
        ]}
      >
        <SimpleLayout
          banner={
            <Route
              path={[ACCOUNT_LOGIN, FORGOT_PASSWORD, RESET_PASSWORD, CREATE_ACCOUNT]}
              component={GpcOptOutBanner}
            />
          }
        >
          <Suspense fallback={<Loader hideText mt={8} />}>
            <Switch>
              <LERoute title="Feedback | Lease End" path={FEEDBACK} component={Feedback} />
              {secureInfoLinkEnabled && (
                <LERoute
                  title="Secure Data Entry | Lease End"
                  path={SECURE_DATA_ENTRY}
                  component={SecureDataEntry}
                />
              )}
              <LERoute path={ACCOUNT_LOGIN} component={AccountLogin} title="Login | Lease End" />
              <LERoute
                path={FORGOT_PASSWORD}
                component={ForgotPassword}
                title="Forgot Password | Lease End"
              />
              <LERoute
                path={RESET_PASSWORD}
                component={ResetPassword}
                title="Reset Password | Lease End"
              />
              <LERoute
                path={CREATE_ACCOUNT}
                component={CreateAccount}
                title="Create Account | Lease End"
              />
            </Switch>
          </Suspense>
        </SimpleLayout>
      </Route>
      <LERoute title="Photo Capture | Lease End" path={PHOTO_CAPTURE}>
        <SimpleLayout>
          <PhotoCapture />
        </SimpleLayout>
      </LERoute>
      <LERoute title="We Listen | Lease End" path={WE_LISTEN}>
        <SimpleLayout background="clay">
          <WeListen />
        </SimpleLayout>
      </LERoute>
      <LERoute title="Marketplace Feedback | Lease End" path={MARKETPLACE_FEEDBACK}>
        <SimpleLayout>
          <MarketplaceFeedback />
        </SimpleLayout>
      </LERoute>
      <LERoute
        title="Verify Insurance | Lease End"
        path={VERIFY_INSURANCE}
        component={VerifyInsurance}
      />
      <Route>
        <MainLayout>
          <Suspense fallback={<Loader hideText mt={8} />}>
            <Switch>
              {/* Uncomment this to see what images have been added to COM */}
              {/* <LERoute path="/image-library" component={ImageLibrary} title="Image Library" /> */}
              <LERoute path={FLOW_ENTRY} component={FlowEntry} title="Flow | Lease End" exact />
              <LERoute path="*" component={PageFor404s} title="404 | Lease End" />
            </Switch>
          </Suspense>
        </MainLayout>
      </Route>
    </Switch>
  );
};

export default Routes;
